import * as React from 'react';

import {Card} from '@spaced-out/ui-design-system/lib/components/Card';
import {Button} from '@spaced-out/ui-design-system/lib/components/Button';
import {
  Badge,
  type BadgeColorType,
} from '@spaced-out/ui-design-system/lib/components/Badge';
import {
  SubTitleLarge,
  BodyLarge,
  BodyMedium,
  JumboSmall,
  TEXT_COLORS,
} from '@spaced-out/ui-design-system/lib/components/Text';

import type {AiTeamCardState, AiTeamMetric} from 'src/types/ai-team';
import {classify} from 'src/utils/classify';

import css from './ai-team-card.css';


export interface AiTeamCardAction {
  label: string;
  iconRightName?: string;
  onClick?: () => void;
  disabled?: boolean;
}

// Generic state-driven card for an AI Team agent. Takes already-translated text;
// no data fetching, no i18n here — wrappers resolve state + strings.
export interface AiTeamCardProps {
  state: AiTeamCardState;
  title: string;
  // Status pill after the title (e.g. "Coming Soon").
  badge?: {text: string; fill?: BadgeColorType};
  // Body for not_enabled / coming_soon / unknown, and the tail after `lead`.
  description?: string;
  // Bold lead sentence for setup_pending / no_active_sequence.
  lead?: string;
  // Body for active.
  metrics?: AiTeamMetric[];
  primaryAction?: AiTeamCardAction;
  secondaryAction?: AiTeamCardAction;
  testId?: string;
}

// CTA states get a filled primary button; live/coming-soon cards a quieter one.
const PRIMARY_BUTTON_TYPE: {
  [state in AiTeamCardState]: 'primary' | 'secondary';
} = {
  not_enabled: 'primary',
  setup_pending: 'primary',
  no_active_sequence: 'primary',
  active: 'secondary',
  coming_soon: 'secondary',
  unknown: 'secondary',
};

function AiTeamCard({
  state,
  title,
  badge,
  description,
  lead,
  metrics,
  primaryAction,
  secondaryAction,
  testId,
}: AiTeamCardProps): React.ReactElement {
  const showMetrics =
    state === 'active' && metrics != null && metrics.length > 0;
  // AC7: unresolvable state renders the definition only — no wrong-state CTA.
  const showActions = state !== 'unknown';

  return (
    <Card
      paddingTop="medium"
      paddingRight="medium"
      paddingBottom="medium"
      paddingLeft="medium"
      classNames={{wrapper: css.card}}
      testId={testId}
    >
      <div className={css.main}>
        <div className={css.heading}>
          <SubTitleLarge>{title}</SubTitleLarge>
          {badge != null && <Badge text={badge.text} fill={badge.fill} />}
        </div>
        {showMetrics ? (
          <div className={css.metrics}>
            {metrics.map((metric, index) => (
              <div
                key={metric.label}
                className={classify(css.metric, {
                  [css.dividedMetric]: index < metrics.length - 1,
                })}
              >
                <BodyMedium color={TEXT_COLORS.secondary}>
                  {metric.label}
                </BodyMedium>
                <JumboSmall>{metric.value}</JumboSmall>
              </div>
            ))}
          </div>
        ) : (
          <BodyLarge color={TEXT_COLORS.secondary}>
            {lead != null && (
              <>
                <span className={css.lead}>{lead}</span>{' '}
              </>
            )}
            {description}
          </BodyLarge>
        )}
      </div>
      {showActions && (
        <div className={css.actions}>
          {primaryAction != null && (
            <Button
              type={PRIMARY_BUTTON_TYPE[state]}
              size="medium"
              iconRightName={primaryAction.iconRightName}
              onClick={primaryAction.onClick}
              disabled={primaryAction.disabled}
            >
              {primaryAction.label}
            </Button>
          )}
          {secondaryAction != null && (
            <Button
              type="tertiary"
              size="medium"
              iconRightName={secondaryAction.iconRightName}
              onClick={secondaryAction.onClick}
              disabled={secondaryAction.disabled}
            >
              {secondaryAction.label}
            </Button>
          )}
        </div>
      )}
    </Card>
  );
}

export default AiTeamCard;
